import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { User, ClipboardCheck, KeyRound, LogOut, Phone, Mail, IndianRupee, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, Camera, Wrench, X, Play, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { usePanelAuth, formatApiError } from './PanelAuthContext';
import EmployeeCompletionForm from '../../components/EmployeeCompletionForm';
import EmployeeServiceTicketsTab from '../../components/EmployeeServiceTicketsTab';
import EmployeeAttendanceTab from '../../components/EmployeeAttendanceTab';
import EmployeeEarningsTab from '../../components/EmployeeEarningsTab';
import StaffToolsLauncher from '../../components/StaffToolsLauncher';
import { Headphones } from 'lucide-react';
export default function EmployeePortal() {
  const { user, panelToken, gwToken, logout, api, bootChecked } = usePanelAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('jobs');
  const [staffLinkCount, setStaffLinkCount] = useState(0);
  useEffect(() => {
    api('GET', '/staff-links').then((r) => setStaffLinkCount((r.data.links || []).length)).catch(() => {});
  }, [api]);

  if (bootChecked && (!gwToken || !panelToken)) return <Navigate to="/official" replace />;
  if (bootChecked && user && user.role !== 'employee') return <Navigate to="/official/admin" replace />;

  const handleLogout = async () => {
    await logout(false);
    nav('/official/portal-selection', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="employee-portal">
      <header className="bg-gradient-to-r from-blue-900 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-300 font-semibold">Employee Portal</div>
            <div className="text-xl sm:text-2xl font-bold mt-0.5">Hi, {user?.name?.split(' ')[0] || 'Technician'} 👋</div>
            <div className="text-xs text-white/60 mt-0.5">{user?.employee_code} · {user?.designation}</div>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="emp-logout"><LogOut size={16} className="mr-1.5" /> Logout</Button>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-1 border-t border-white/10">
          {[
            { id: 'jobs', label: 'My Jobs', Icon: ClipboardCheck },
            { id: 'attendance', label: 'Attendance', Icon: MapPin },
            { id: 'earnings', label: 'Earnings', Icon: IndianRupee },
            { id: 'tickets', label: 'Service Tickets', Icon: AlertCircle },
            ...(staffLinkCount > 0 ? [{ id: 'staff-tools', label: 'Staff Tools', Icon: Headphones }] : []),
            { id: 'profile', label: 'Profile', Icon: User },
            { id: 'password', label: 'Password', Icon: KeyRound },
          ].map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`emp-tab-${id}`}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === id ? 'border-blue-400 text-white' : 'border-transparent text-white/60 hover:text-white'}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {tab === 'jobs' && <JobsTab api={api} />}
        {tab === 'attendance' && <EmployeeAttendanceTab api={api} />}
        {tab === 'earnings' && <EmployeeEarningsTab api={api} />}
        {tab === 'tickets' && <EmployeeServiceTicketsTab api={api} />}
        {tab === 'staff-tools' && (
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="emp-staff-tools">
            <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2"><Headphones size={18} className="text-emerald-600" /> EntoRid Staff Tools</h2>
            <p className="text-sm text-slate-500 mb-4">Your authorized tools — click to open.</p>
            <StaffToolsLauncher api={api} />
          </div>
        )}
        {tab === 'profile' && <ProfileTab user={user} />}
        {tab === 'password' && <PasswordTab api={api} />}
      </main>
    </div>
  );
}

function JobsTab({ api }) {
  const { panelToken } = usePanelAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [activeComp, setActiveComp] = useState(null);
  const showToast = (t, m) => { setToast({ t, m }); setTimeout(() => setToast(null), 3000); };
  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api('GET', '/employee/assignments'); setRows(res.data || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (job, status) => {
    try {
      await api('PATCH', `/employee/jobs/${job.id}`, { status });
      showToast('success', `Status: ${status.replace(/_/g, ' ')}`);
      load();
    } catch (e) { showToast('error', formatApiError(e)); }
  };

  const STATUS_OPTIONS = [
    { id: 'on_the_way', label: '🚗 On the Way', cls: 'bg-amber-600 hover:bg-amber-700' },
    { id: 'in_progress', label: '🛠 In Progress', cls: 'bg-purple-600 hover:bg-purple-700' },
    { id: 'completed', label: '✅ Completed', cls: 'bg-emerald-600 hover:bg-emerald-700' },
  ];

  return (
    <div data-testid="jobs-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-900">My Assigned Jobs ({rows.length})</h2>
        <Button variant="outline" size="sm" onClick={load}><Loader2 size={14} className="mr-1.5" />Refresh</Button>
      </div>

      {toast && (
        <div className={`mb-3 rounded-lg p-3 text-sm flex items-center gap-2 ${toast.t === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {toast.t === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {toast.m}
        </div>
      )}

      {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
       rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <ClipboardCheck size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No jobs assigned yet.</p>
          <p className="text-xs text-slate-400 mt-2">Admin will assign jobs from the bookings tab.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(j => {
            const masked = j._details_masked;
            const showCall = !masked && j.customer_phone;
            return (
            <div key={j.id} className={`bg-white rounded-xl p-4 border shadow-sm ${j.invoice_generated ? 'border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white' : 'border-slate-200'}`} data-testid={`job-${j.booking_code}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold">{j.booking_code}</span>
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{(j.status || '').replace(/_/g, ' ')}</span>
                    {j.invoice_generated && (
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1" data-testid={`job-${j.booking_code}-invoiced`}>
                        ✅ Work Done · Invoice {j.invoice_number}
                      </span>
                    )}
                    {masked && (
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200" title="Customer privacy mode is ON. Ask Super Admin to flip the toggle if you need full details.">
                        🔒 Privacy Mode
                      </span>
                    )}
                  </div>
                  <div className="font-bold mt-1">{j.customer_name} · {j.service_label}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    📅 {j.scheduled_date} {j.scheduled_time_slot ? `· ${j.scheduled_time_slot}` : ''}
                    {showCall && <> · 📱 {j.customer_phone}</>}
                  </div>
                  <div className="text-xs text-slate-500">📍 {j.customer_address || j.customer_city}</div>
                  {!masked && j.customer_notes && <div className="text-xs text-slate-600 mt-1 italic">Note: {j.customer_notes}</div>}
                </div>
                {!masked && (
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Amount</div>
                    <div className="text-lg font-bold text-emerald-700">₹{(j.final_amount || 0).toLocaleString('en-IN')}</div>
                  </div>
                )}
              </div>
              {j.invoice_generated && (
                <div className="mt-3 pt-3 border-t border-emerald-200 text-xs bg-emerald-50 rounded-lg px-3 py-2 text-emerald-800 inline-flex items-center gap-2" data-testid={`job-${j.booking_code}-done-banner`}>
                  🎉 <b>Service completed and invoice sent to customer.</b> No further action required.
                </div>
              )}
              {!j.invoice_generated && j.status !== 'completed' && j.status !== 'cancelled' && j.status !== 'awaiting_approval' && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                  {STATUS_OPTIONS.filter(s => s.id !== j.status && s.id !== 'completed').map(s => (
                    <button key={s.id} onClick={() => updateStatus(j, s.id)} className={`${s.cls} text-white text-xs font-semibold px-3 py-1.5 rounded-lg`} data-testid={`job-${j.booking_code}-${s.id}`}>{s.label}</button>
                  ))}
                  <button onClick={() => setActiveComp(j)} className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg" data-testid={`job-${j.booking_code}-complete`}>📋 Submit Completion</button>
                  {(j.customer_address || j.customer_city) && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(j.customer_address || j.customer_city)}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`job-${j.booking_code}-navigate`}
                      className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
                    >
                      🧭 Navigate
                    </a>
                  )}
                  {showCall && (
                    <a href={`tel:${j.customer_phone}`} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1"><Phone size={12} /> Call</a>
                  )}
                </div>
              )}
              {j.status === 'awaiting_approval' && !j.invoice_generated && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800 inline-flex items-center gap-2">
                  ⏳ <b>Awaiting Admin Approval</b> — invoice & warranty will fire once approved.
                </div>
              )}
              {!j.invoice_generated && <WorkOrderPanel job={j} reload={load} />}
            </div>
            );
          })}
        </div>
      )}
      {activeComp && <EmployeeCompletionForm booking={activeComp} token={panelToken} onCancel={() => setActiveComp(null)} onSubmitted={() => { setActiveComp(null); showToast('success', 'Sent for admin approval ✓'); load(); }} />}
    </div>
  );
}

function ProfileTab({ user }) {
  if (!user) return null;
  return (
    <div data-testid="profile-tab" className="bg-white rounded-2xl p-6 border border-slate-200">
      <h2 className="text-lg font-bold text-slate-900 mb-4">My Profile</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div><div className="text-xs text-slate-500 font-semibold uppercase">Employee Code</div><div className="font-bold text-slate-900">{user.employee_code || '—'}</div></div>
        <div><div className="text-xs text-slate-500 font-semibold uppercase">Status</div><div className="font-bold text-emerald-700 uppercase">{user.status}</div></div>
        <div><div className="text-xs text-slate-500 font-semibold uppercase">Name</div><div>{user.name}</div></div>
        <div><div className="text-xs text-slate-500 font-semibold uppercase">Designation</div><div>{user.designation}</div></div>
        <div className="flex items-center gap-2"><Mail size={14} className="text-slate-400" /><span>{user.email}</span></div>
        <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /><span>{user.mobile}</span></div>
        <div><div className="text-xs text-slate-500 font-semibold uppercase">Department</div><div>{user.department}</div></div>
        <div><div className="text-xs text-slate-500 font-semibold uppercase">Joined</div><div>{user.joining_date}</div></div>
      </div>
      <div className="mt-6 pt-4 border-t">
        <h3 className="text-sm font-bold text-slate-700 mb-2">My Permissions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
          {Object.entries(user.permissions || {}).map(([k, v]) => (
            <div key={k} className={`px-2 py-1 rounded ${v ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'}`}>
              {v ? '✓' : '·'} {k.replace(/_/g, ' ').replace(/^can /, '')}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PasswordTab({ api }) {
  const [cur, setCur] = useState(''); const [next, setNext] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false); const [toast, setToast] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api('POST', '/change-password', { current_password: cur, new_password: next });
      setToast({ t: 'success', m: 'Password changed' }); setCur(''); setNext('');
    } catch (e) { setToast({ t: 'error', m: formatApiError(e) }); }
    finally { setBusy(false); setTimeout(() => setToast(null), 3500); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 border border-slate-200 max-w-md" data-testid="emp-password-form">
      <h2 className="text-lg font-bold mb-4">Change Password</h2>
      {toast && (
        <div className={`mb-3 rounded-lg p-3 text-sm flex items-center gap-2 ${toast.t === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {toast.t === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {toast.m}
        </div>
      )}
      <div className="space-y-3">
        <div><Label>Current Password</Label><Input type={show ? 'text' : 'password'} value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="emp-cur-pw" /></div>
        <div>
          <Label>New Password (min 8)</Label>
          <div className="relative">
            <Input type={show ? 'text' : 'password'} value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} data-testid="emp-new-pw" />
            <button type="button" onClick={() => setShow(s => !s)} className="absolute inset-y-0 right-0 px-3 text-slate-500">{show ? <EyeOff size={14} /> : <Eye size={14} />}</button>
          </div>
        </div>
      </div>
      <Button type="submit" disabled={busy || next.length < 8} className="mt-4 bg-emerald-600 hover:bg-emerald-700" data-testid="emp-change-pw-submit">
        {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Change Password
      </Button>
    </form>
  );
}


/**
 * Work Order Panel — collapsible per-job UI for the technician to:
 *  1. Start a work order
 *  2. Upload before/after photos
 *  3. Fill chemicals/areas/notes and complete
 *
 * Photo upload uses FormData against /api/panel/employee/work-orders/{id}/photo
 */
function WorkOrderPanel({ job, reload }) {
  const { panelToken } = usePanelAuth();
  const [open, setOpen] = useState(false);
  const [wo, setWo] = useState(job.work_order || null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    chemicals_used: '',
    areas_treated: '',
    observations: '',
    completion_notes: '',
  });
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api/panel`;

  useEffect(() => {
    setWo(job.work_order || null);
    if (job.work_order) {
      setForm({
        chemicals_used: job.work_order.chemicals_used || '',
        areas_treated: job.work_order.areas_treated || '',
        observations: job.work_order.observations || '',
        completion_notes: job.work_order.completion_notes || '',
      });
    }
  }, [job]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${apiBase}/employee/work-orders/${job.id}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${panelToken}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setWo(d.work_order);
      await reload();
    } catch (e) { alert('Failed: ' + e.message); } finally { setBusy(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be <5MB'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', file);
      const r = await fetch(`${apiBase}/employee/work-orders/${job.id}/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${panelToken}` },
        body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      const d = await r.json();
      setWo(prev => ({ ...(prev || {}), photos: [...((prev && prev.photos) || []), d.photo] }));
      await reload();
    } catch (e) { alert('Upload failed: ' + e.message); } finally { setBusy(false); }
  };

  const complete = async () => {
    if (!form.areas_treated.trim()) { alert('Please fill "areas treated" before completing'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${apiBase}/employee/work-orders/${job.id}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${panelToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      const d = await r.json();
      setWo(d.work_order);
      await reload();
      setOpen(false);
    } catch (e) { alert('Failed: ' + e.message); } finally { setBusy(false); }
  };

  const photos = (wo && wo.photos) || [];
  const completed = wo && wo.status === 'completed';

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
        data-testid={`wo-toggle-${job.booking_code}`}
      >
        <Wrench size={12} /> {open ? 'Hide' : (completed ? 'View' : 'Open')} Work Order
        {photos.length > 0 && <span className="text-slate-400 font-normal">({photos.length} photo{photos.length > 1 ? 's' : ''})</span>}
      </button>

      {open && (
        <div className="mt-3 bg-slate-50 rounded-lg p-3 space-y-3 text-sm">
          {!wo && (
            <Button size="sm" onClick={start} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`wo-start-${job.booking_code}`}>
              {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Play size={13} className="mr-1.5" />} Start Work Order
            </Button>
          )}

          {wo && (
            <>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => beforeRef.current?.click()}
                  className="px-3 py-2 rounded-lg bg-white border border-slate-300 text-xs font-semibold inline-flex items-center gap-1 hover:bg-slate-100"
                  disabled={busy || completed}
                  data-testid={`wo-photo-before-${job.booking_code}`}
                >
                  <Camera size={13} /> Add Before Photo
                </button>
                <input ref={beforeRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload('before', e.target.files?.[0])} />
                <button
                  onClick={() => afterRef.current?.click()}
                  className="px-3 py-2 rounded-lg bg-white border border-slate-300 text-xs font-semibold inline-flex items-center gap-1 hover:bg-slate-100"
                  disabled={busy || completed}
                  data-testid={`wo-photo-after-${job.booking_code}`}
                >
                  <Camera size={13} /> Add After Photo
                </button>
                <input ref={afterRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload('after', e.target.files?.[0])} />
              </div>

              {photos.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {photos.map(p => (
                    <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer" className="relative aspect-square rounded overflow-hidden border border-slate-200 block">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt={p.kind} className="w-full h-full object-cover" />
                      <span className="absolute top-1 left-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded uppercase">{p.kind}</span>
                    </a>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Areas Treated *</Label>
                  <Input value={form.areas_treated} onChange={(e) => setForm({ ...form, areas_treated: e.target.value })} disabled={completed} placeholder="Kitchen, bathroom, balcony" data-testid={`wo-areas-${job.booking_code}`} />
                </div>
                <div>
                  <Label className="text-xs">Chemicals Used</Label>
                  <Input value={form.chemicals_used} onChange={(e) => setForm({ ...form, chemicals_used: e.target.value })} disabled={completed} placeholder="e.g. Imidacloprid 5%" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Observations</Label>
                  <Textarea rows={2} value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} disabled={completed} placeholder="Live nest under sink, heavy infestation in west wall…" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Completion Notes</Label>
                  <Textarea rows={2} value={form.completion_notes} onChange={(e) => setForm({ ...form, completion_notes: e.target.value })} disabled={completed} placeholder="Customer informed about 7-day re-spray instructions" />
                </div>
              </div>

              {!completed && (
                <Button onClick={complete} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid={`wo-complete-${job.booking_code}`}>
                  {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />} Mark Complete
                </Button>
              )}
              {completed && (
                <div className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1.5">
                  <CheckCircle2 size={13} /> Completed on {(wo.completed_at || '').slice(0, 16).replace('T', ' ')}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
